/**
 * useLiveRecording — owns the post-recording local state for the Live
 * Recording dialog (audio blob URLs, final transcript text, file
 * attachments, AI-suggested titles, refs).
 *
 * This is a stepping-stone toward removing the consolidated `s` state-bag
 * prop from LiveRecordingDialog: instead of the parent owning these
 * 12 fields and prop-drilling them, the dialog calls `useLiveRecording()`
 * and self-sources. The handlers that depend on these fields will move
 * into the dialog in a follow-up — for now, parent-owned handlers
 * (suggestTitles, attachFile, downloadAudio, downloadTranscript, …)
 * still operate via setters threaded back from this hook.
 *
 * @returns {{
 *   recordedAudioUrl: string | null,
 *   setRecordedAudioUrl: (v: string | null) => void,
 *   recordedAudioBlob: Blob | null,
 *   setRecordedAudioBlob: (v: Blob | null) => void,
 *   finalTranscriptText: string,
 *   setFinalTranscriptText: (v: string) => void,
 *   attachedFiles: Array<{id: string, name: string, url: string}>,
 *   setAttachedFiles: (v: any) => void,
 *   attachUploading: boolean,
 *   setAttachUploading: (v: boolean) => void,
 *   attachFileRef: React.MutableRefObject<HTMLInputElement|null>,
 *   titleSuggestions: string[],
 *   setTitleSuggestions: (v: string[]) => void,
 *   suggestingTitle: boolean,
 *   setSuggestingTitle: (v: boolean) => void,
 *   transcriptContainerRef: React.MutableRefObject<HTMLDivElement|null>,
 *   reset: () => void,
 * }}
 */
import { useRef, useState, useCallback } from 'react';

export const useLiveRecording = () => {
  const [recordedAudioUrl, setRecordedAudioUrl] = useState(null);
  const [recordedAudioBlob, setRecordedAudioBlob] = useState(null);
  const [finalTranscriptText, setFinalTranscriptText] = useState('');
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [attachUploading, setAttachUploading] = useState(false);
  const [titleSuggestions, setTitleSuggestions] = useState([]);
  const [suggestingTitle, setSuggestingTitle] = useState(false);
  const attachFileRef = useRef(null);
  const transcriptContainerRef = useRef(null);

  // Clears every post-recording asset so the dialog can be reused for
  // another lecture without leaking state from the previous one.
  // Used by the parent's save handler on success and by the close button.
  const reset = useCallback(() => {
    setRecordedAudioUrl(null);
    setRecordedAudioBlob(null);
    setFinalTranscriptText('');
    setAttachedFiles([]);
    setAttachUploading(false);
    setTitleSuggestions([]);
    setSuggestingTitle(false);
  }, []);

  return {
    recordedAudioUrl, setRecordedAudioUrl,
    recordedAudioBlob, setRecordedAudioBlob,
    finalTranscriptText, setFinalTranscriptText,
    attachedFiles, setAttachedFiles,
    attachUploading, setAttachUploading,
    attachFileRef,
    titleSuggestions, setTitleSuggestions,
    suggestingTitle, setSuggestingTitle,
    transcriptContainerRef,
    reset,
  };
};

export default useLiveRecording;
